import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, Mountain, Shield, 
  Check, X as XIcon, ChevronRight, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.includes('dropbox.com')) {
    let converted = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    converted = converted.replace(/[&?]dl=0/, '');
    return converted;
  }
  return url;
};

const getUserLanguage = () => {
  // Sync with manual language toggle stored by App.js
  const override = localStorage.getItem('arctic_riders_lang');
  if (override === 'sv' || override === 'en') return override;
  return 'sv'; // Default Swedish (Sweden-only site)
};

const TourProductPage = () => {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [riders, setRiders] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", requests: "" });
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [lang, setLang] = useState(getUserLanguage());
  const [lightbox, setLightbox] = useState(null); // {src, index}
  const [variantId, setVariantId] = useState(null);

  // React to manual language toggle from header / mobile menu
  useEffect(() => {
    const onLangChange = () => setLang(getUserLanguage());
    window.addEventListener('langchange', onLangChange);
    window.addEventListener('storage', onLangChange);
    return () => {
      window.removeEventListener('langchange', onLangChange);
      window.removeEventListener('storage', onLangChange);
    };
  }, []);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const res = await axios.get(`${API}/tour-products/${slug}`);
        setTour(res.data);
      } catch (e) {
        setTour(null);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [slug]);

  const t = (en, sv) => lang === 'sv' && sv ? sv : en;
  const spotsLeft = tour ? (tour.max_riders || 4) - (tour.booked_riders || 0) : 0;
  const isEur = tour?.currency === 'eur';
  const formatPrice = (price) => isEur ? `€${Number(price).toLocaleString()}` : `${Number(price).toLocaleString('sv-SE')} kr`;
  const backPath = `/`;
  const backLabel = t("Back", "Tillbaka");

  // Compute selected variant + pricing/availability
  const hasVariants = Array.isArray(tour?.variants) && tour.variants.length > 0;
  const selectedVariant = hasVariants && variantId
    ? tour.variants.find(v => v.id === variantId)
    : null;
  const effectivePrice = hasVariants
    ? (selectedVariant ? Number(selectedVariant.price) : null)
    : Number(tour?.price || 0);
  const variantSpotsLeft = selectedVariant
    ? Math.max(0, Number(selectedVariant.max_riders || 0) - Number(selectedVariant.booked_riders || 0))
    : null;

  const handleBook = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error(t("Fill in all fields", "Fyll i alla fält")); return; }
    if (!policyAccepted) { toast.error(t("Accept the policy", "Godkänn villkoren")); return; }
    if (hasVariants && !variantId) { toast.error(t("Please select an option", "Vänligen välj ett alternativ")); return; }
    const limit = variantSpotsLeft !== null ? variantSpotsLeft : spotsLeft;
    if (riders > limit) { toast.error(t("Not enough spots", "Inte tillräckligt med platser")); return; }
    
    setBooking(true);
    try {
      const res = await axios.post(`${API}/tour-products/${tour.id}/book`, {
        tour_id: tour.id,
        riders,
        variant_id: variantId || null,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        special_requests: form.requests,
        policy_accepted: true,
        lang: lang || 'sv'
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Booking failed", "Bokningen misslyckades"));
      setBooking(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#EA580C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!tour) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="font-anton text-3xl text-white mb-4">{t("Tour Not Found", "Turen hittades inte")}</h1>
        <Link to="/" className="text-[#EA580C] hover:underline font-manrope">{t("Back", "Tillbaka")}</Link>
      </div>
    </div>
  );

  const diffColor = { easy: "text-green-400 bg-green-400/10", medium: "text-yellow-400 bg-yellow-400/10", hard: "text-red-400 bg-red-400/10" };
  const diffLabel = { easy: t("Easy", "Enkel"), medium: t("Medium", "Medel"), hard: t("Hard", "Svår") };
  const included = lang === 'sv' && tour.included_sv?.length ? tour.included_sv : tour.included || [];
  const excluded = lang === 'sv' && tour.excluded_sv?.length ? tour.excluded_sv : tour.excluded || [];
  const highlights = lang === 'sv' && tour.highlights_sv?.length ? tour.highlights_sv : tour.highlights || [];
  const description = t(tour.description, tour.description_sv);
  const routeDesc = t(tour.route_description, tour.route_description_sv);
  const meetingPoint = t(tour.meeting_point, tour.meeting_point_sv);
  const tourDate = tour.date ? new Date(tour.date).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="tour-product-page">
      <Toaster position="top-center" theme="dark" />
      
      {/* Header */}
      <header className="bg-[#171717] border-b border-white/10 px-5 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to={backPath} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors font-manrope text-sm" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </Link>
          <img src="/images/logo_ar.webp" alt="Arctic Riders" className="h-8 sm:h-10" />
        </div>
      </header>

      {/* Hero image */}
      {tour.image && (
        <div className="relative h-56 sm:h-72 md:h-96 overflow-hidden">
          <img src={resolveImageUrl(tour.image)} alt={tour.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
      )}

      {/* Gallery */}
      {Array.isArray(tour.gallery) && tour.gallery.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-6 sm:pt-8" data-testid="tour-gallery">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            {tour.gallery.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightbox({ src: resolveImageUrl(src), index: i })}
                className="group relative overflow-hidden border border-white/5 hover:border-[#EA580C]/50 transition-colors"
                data-testid={`gallery-thumb-${i}`}
              >
                <img
                  src={resolveImageUrl(src)}
                  alt={`${tour.title} ${i + 1}`}
                  loading="lazy"
                  className="w-full h-24 sm:h-32 md:h-36 object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
            aria-label="Close"
            data-testid="lightbox-close"
          >×</button>
          {tour.gallery && tour.gallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const prev = (lightbox.index - 1 + tour.gallery.length) % tour.gallery.length;
                  setLightbox({ src: resolveImageUrl(tour.gallery[prev]), index: prev });
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl"
                aria-label="Previous"
              >‹</button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = (lightbox.index + 1) % tour.gallery.length;
                  setLightbox({ src: resolveImageUrl(tour.gallery[next]), index: next });
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl"
                aria-label="Next"
              >›</button>
            </>
          )}
          <img src={lightbox.src} alt="" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          
          {/* Left: Details */}
          <div className="md:col-span-2 space-y-8">
            {/* Title & meta */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {tour.difficulty && (
                  <span className={`px-2.5 py-1 text-xs uppercase font-bold ${diffColor[tour.difficulty]}`}>
                    {diffLabel[tour.difficulty]}
                  </span>
                )}
                {tour.status === 'sold_out' && (
                  <span className="px-2.5 py-1 text-xs uppercase font-bold bg-red-500/20 text-red-400">{t("Sold Out", "Fullbokad")}</span>
                )}
              </div>
              <h1 className="font-anton text-3xl sm:text-4xl md:text-5xl text-white leading-tight" data-testid="tour-title">
                {t(tour.title, tour.title_sv)}
              </h1>
            </div>

            {/* Quick info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {(() => {
                // Calculate duration in days
                let durationLabel = null;
                if (tour.end_date && tour.date) {
                  try {
                    const d1 = new Date(tour.date.slice(0,10));
                    const d2 = new Date(tour.end_date.slice(0,10));
                    const days = Math.round((d2 - d1) / (1000*60*60*24)) + 1;
                    if (days > 1) durationLabel = lang === 'sv' ? `${days} dagar` : `${days} days`;
                  } catch (_err) { /* ignore date parse errors */ }
                }
                const items = [
                  { icon: Calendar, label: tour.duration_label ? (lang === 'sv' ? tour.duration_label : (tour.duration_label_en || tour.duration_label)) : tourDate, key: t("When", "När") },
                  durationLabel ? { icon: Clock, label: durationLabel, key: t("Duration", "Längd") } : null,
                  { icon: Users, label: t(`Max ${tour.max_riders} riders`, `Max ${tour.max_riders} förare`), key: t("Group size", "Gruppstorlek") },
                  tour.distance ? { icon: Mountain, label: tour.distance, key: t("Distance", "Distans") } : null,
                  (!tour.duration_label && !durationLabel) ? { icon: Clock, label: `${tour.time_start} - ${tour.time_end}`, key: t("Time", "Tid") } : null
                ].filter(Boolean);
                return items.map((item, i) => (
                  <div key={i} className="bg-[#171717] border border-white/10 p-4 sm:p-5 flex items-start gap-3 sm:gap-4 hover:border-[#EA580C]/40 transition-colors">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#EA580C]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#EA580C]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wider font-manrope font-medium mb-1">{item.key}</div>
                      <div className="text-white text-sm sm:text-base font-anton leading-tight">{item.label}</div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Description */}
            {description && (
              <div>
                <h2 className="font-anton text-xl text-white mb-3">{t("About This Tour", "Om Denna Tur")}</h2>
                <div className="font-manrope text-white/70 text-sm sm:text-base leading-relaxed whitespace-pre-line">{description}</div>
              </div>
            )}

            {/* Route */}
            {routeDesc && (
              <div>
                <h2 className="font-anton text-xl text-white mb-3">{t("Route", "Rutt")}</h2>
                <div className="font-manrope text-white/70 text-sm sm:text-base leading-relaxed whitespace-pre-line">{routeDesc}</div>
              </div>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <div>
                <h2 className="font-anton text-xl text-white mb-3">{t("Highlights", "Höjdpunkter")}</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2.5 bg-[#171717] border border-white/5 p-3">
                      <Star className="w-4 h-4 text-[#EA580C] flex-shrink-0" />
                      <span className="text-white/80 text-sm font-manrope">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Included / Excluded */}
            <div className="grid sm:grid-cols-2 gap-6">
              {included.length > 0 && (
                <div>
                  <h2 className="font-anton text-xl text-white mb-3">{t("Included", "Ingår")}</h2>
                  <ul className="space-y-2">
                    {included.map((item, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-white/70 text-sm font-manrope">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {excluded.length > 0 && (
                <div>
                  <h2 className="font-anton text-xl text-white mb-3">{t("Not Included", "Ingår Ej")}</h2>
                  <ul className="space-y-2">
                    {excluded.map((item, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-white/50 text-sm font-manrope">
                        <XIcon className="w-4 h-4 text-red-400 flex-shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Meeting point */}
            {meetingPoint && (
              <div className="bg-[#171717] border border-white/5 p-4 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#EA580C] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-anton text-base text-white mb-1">{t("Meeting Point", "Mötesplats")}</h3>
                  <p className="text-white/60 text-sm font-manrope">{meetingPoint}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: Booking card */}
          <div className="md:col-span-1">
            <div className="sticky top-6 bg-[#171717] border border-white/10 p-5 sm:p-6 space-y-5" data-testid="tour-booking-card">
              {hasVariants ? (
                <div data-testid="variant-selector">
                  <div className="text-white/40 text-xs uppercase tracking-wider font-manrope mb-2">
                    {t("Choose an option", "Välj alternativ")}
                  </div>
                  <div className="space-y-2">
                    {tour.variants.map((v) => {
                      const vSpots = Math.max(0, Number(v.max_riders || 0) - Number(v.booked_riders || 0));
                      const vName = (lang === 'sv' && v.name_sv) ? v.name_sv : (v.name || v.name_sv || t("Option", "Alternativ"));
                      const isSelected = variantId === v.id;
                      const disabled = vSpots <= 0;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => setVariantId(v.id)}
                          className={`w-full text-left border p-3 transition-colors ${isSelected ? 'border-[#EA580C] bg-[#EA580C]/10' : 'border-white/10 hover:border-white/30 bg-[#0A0A0A]'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                          data-testid={`variant-option-${v.id}`}
                        >
                          <div className="flex items-baseline justify-between mb-1">
                            <span className="text-white font-anton text-sm sm:text-base">{vName}</span>
                            <span className="text-[#EA580C] font-anton text-lg sm:text-xl ml-3">{formatPrice(v.price)}</span>
                          </div>
                          <div className={`text-xs font-manrope ${vSpots > 0 ? 'text-white/50' : 'text-red-400'}`}>
                            {vSpots > 0 ? t(`${vSpots} spot(s) left`, `${vSpots} plats(er) kvar`) : t("Sold out", "Fullbokad")}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-white/40 text-xs uppercase tracking-wider font-manrope mb-1">{t("Price per rider", "Pris per förare")}</div>
                  <div className="font-anton text-3xl sm:text-4xl text-[#EA580C]">{formatPrice(tour.price)}</div>
                </div>
              )}

              {!hasVariants && (
                <div className="bg-[#0A0A0A] border border-white/10 p-3 text-center">
                  <span className={`text-sm font-manrope ${spotsLeft > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {spotsLeft > 0 ? t(`${spotsLeft} spot(s) left`, `${spotsLeft} plats(er) kvar`) : t("Sold out", "Fullbokad")}
                  </span>
                </div>
              )}

              {(() => {
                const currentSpots = variantSpotsLeft !== null ? variantSpotsLeft : spotsLeft;
                const canBook = currentSpots > 0 && tour.status === 'published' && (!hasVariants || selectedVariant);
                if (!canBook) {
                  if (hasVariants && !selectedVariant) {
                    return <p className="text-white/50 text-sm text-center font-manrope">{t("Select an option above to continue", "Välj ett alternativ ovan för att fortsätta")}</p>;
                  }
                  return null;
                }
                const lineTotal = (effectivePrice || 0) * riders;
                return (
                <>
                  <div>
                    <Label className="text-white/70 text-sm">{t("Riders", "Antal förare")}</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <button onClick={() => setRiders(Math.max(1, riders - 1))} className="w-10 h-10 bg-[#0A0A0A] border border-white/10 text-white flex items-center justify-center">-</button>
                      <span className="font-anton text-xl text-white w-8 text-center">{riders}</span>
                      <button onClick={() => setRiders(Math.min(currentSpots, riders + 1))} className="w-10 h-10 bg-[#0A0A0A] border border-white/10 text-white flex items-center justify-center">+</button>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4 space-y-3">
                    <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder={t("Full name", "Fullständigt namn")} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="tour-book-name" />
                    <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="tour-book-email" />
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder={t("Phone", "Telefon")} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="tour-book-phone" />
                    <textarea value={form.requests} onChange={(e) => setForm({...form, requests: e.target.value})} placeholder={t("Special requests (optional)", "Särskilda önskemål (valfritt)")} rows={2} className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 text-sm" />
                  </div>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} className="mt-1 w-4 h-4 accent-[#EA580C]" data-testid="tour-policy-checkbox" />
                    <span className="text-white/60 text-xs leading-relaxed">
                      {t("I accept the ", "Jag godkänner ")}
                      <a href="/villkor" target="_blank" className="text-[#EA580C] hover:underline">{t("Terms & Conditions", "Villkoren")}</a>
                    </span>
                  </label>

                  <div className="bg-[#0A0A0A] border border-white/10 p-3 flex justify-between items-center">
                    <span className="text-white/60 text-sm">Total</span>
                    <span className="font-anton text-2xl text-[#EA580C]">{formatPrice(lineTotal)}</span>
                  </div>

                  <Button onClick={handleBook} disabled={booking} className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-anton uppercase tracking-wider py-6 text-lg" data-testid="tour-book-button">
                    {booking ? (t("Processing...", "Bearbetar...")) : (t("Book Now", "Boka Nu"))}
                  </Button>
                </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourProductPage;
